import React, { useState, useEffect } from 'react';
import { Modal, Box, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel } from '@mui/material';
import { postRequest, showMessage } from '../../config';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../reducers/modalReducer';

const CreateSubscriptionPlan = ({ selectedPlan, fetchPlans, modalType }) => {
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        description: '',
        interval: 'year',
        isActive: true
    });
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedPlan) {
            setFormData({
                name: selectedPlan.name || '',
                price: selectedPlan.price || '',
                description: selectedPlan.description || '',
                interval: selectedPlan.interval || 'year',
                isActive: selectedPlan.isActive !== false
            });
        } else {
            setFormData({
                name: '',
                price: '',
                description: '',
                interval: 'year',
                isActive: true
            });
        }
    }, [selectedPlan]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let processedValue = value;

        // Convert plan name to uppercase
        if (name === 'name' && value) {
            processedValue = value.toUpperCase();
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : processedValue
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (selectedPlan) {
                // Update existing plan
                await postRequest(`/payment/update-subscription-plan/${selectedPlan._id}`, formData);
                showMessage('Subscription plan updated successfully');
            } else {
                // Create new plan
                await postRequest('/payment/subscription-plan', formData);
                showMessage('Subscription plan created successfully');
            }
            fetchPlans();
            setFormData({
                name: '',
                price: '',
                description: '',
                interval: 'year',
                isActive: true
            });
            dispatch(toggleModal({ open: '' }));
        } catch (error) {
            console.error('Error saving subscription plan:', error);
            showMessage('Error saving subscription plan');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={modalType === 'createSubscriptionPlan'}
            onClose={() => dispatch(toggleModal({ open: '' }))}
            aria-labelledby="create-subscription-plan-modal"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 500,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxHeight: '80vh',
                    overflowY: 'auto'
                }}
            >
                <Typography variant="h6" component="h2" gutterBottom>
                    {selectedPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Plan Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />

                    <TextField
                        fullWidth
                        label="Price"
                        name="price"
                        type="number"
                        value={formData.price}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />


                    <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        margin="normal"
                        multiline
                        rows={3}
                    />

                    <FormControl fullWidth margin="normal">
                        <InputLabel>Interval</InputLabel>
                        <Select
                            name="interval"
                            value={formData.interval}
                            onChange={handleChange}
                            label="Interval"
                        >
                            <MenuItem value="month">Monthly</MenuItem>
                            <MenuItem value="year">Yearly</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={handleChange}
                                name="isActive"
                            />
                        }
                        label="Active"
                        sx={{ mt: 2 }}
                    />

                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => dispatch(toggleModal({ open: '' }))}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (selectedPlan ? 'Update' : 'Create')}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default CreateSubscriptionPlan;
